import clonedeep from 'lodash.clonedeep';
import { UpdateWidget } from 'modules/Editor/store/infographSlice.types';
import { PageId, WidgetId } from 'types/idTypes';
import { InfographState, WidgetsMap } from 'types/infographTypes';
import { WidgetType } from 'types/widget.types';
import { calculateAspectRatioFit } from 'utils/calculateAspectRatioFit';
import { setAllMatchingKeys } from 'utils/setAllPropertyValues';
import { ImageWidgetData } from 'widgets/ImageWidget/ImageWidget.types';
import { getWidgetTypeFromId } from 'widgets/Widget.helpers';

export interface ImageMeta {
  url: string;
  isDark: boolean | null;
  width?: number;
  height?: number;
}

export const selectAllPageBackgroundDataFromData = (pageId: PageId, infograph: InfographState) => {
  if (!pageId || !infograph.pages[pageId]) return { background: '', gradientBackground: null };

  return {
    background: infograph.pages[pageId].background,
    gradientBackground: infograph.pages[pageId].gradientBackground,
    backgroundPattern: infograph.pages[pageId].backgroundPattern,
  };
};

/**
 * Given a URL, returns a Promise that resolves with an HTMLImageElement containing
 * the image's metadata, or rejects with an error if the image fails to load.
 */
const getMeta = (url: string) =>
  new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = (err) => reject(err);
    img.src = url;
  });

/**
 * Given a map of widgets and a logo url, this function goes through each widget
 * and checks if the alt text of the image is 'logo'. If it is, it replaces the
 * image with the given logo and adjusts the width and height of the widget
 * accordingly.
 *
 * @param widgets - A map of widgets
 * @param logo - The url of the logo image.
 * @returns An array of update widgets.
 */
export const applyLogo = async (widgets: WidgetsMap, logo: ImageMeta): Promise<UpdateWidget[]> => {
  const updateWidgets: UpdateWidget[] = [];
  const widgetIds: WidgetId[] = Object.keys(widgets);

  for (let i = 0; i < widgetIds.length; i++) {
    const widgetId = widgetIds[i];
    const isReplaceable = getWidgetTypeFromId(widgetId) === WidgetType.Image;
    if (isReplaceable) {
      const widgetData = widgets[widgetId] as ImageWidgetData;
      const hasLogoReplace = widgetData?.tags?.includes('logo') ?? false;

      if (hasLogoReplace) {
        const widgetData = clonedeep(widgets[widgetId]);
        setAllMatchingKeys(widgetData, 'url', logo.url);

        let { width, height } = logo;
        if (!width || !height) {
          const newImage = await getMeta(logo.url);
          width = newImage.width;
          height = newImage.height;
        }

        const newSize = calculateAspectRatioFit(width, height, widgetData.widthPx, widgetData.heightPx);

        setAllMatchingKeys(widgetData, 'widthPx', newSize.width);
        setAllMatchingKeys(widgetData, 'heightPx', newSize.height);

        updateWidgets.push({ widgetId, widgetData: widgetData });
      }
    }
  }

  return updateWidgets;
};
